import * as SelectPrimitive from "@radix-ui/react-select";
import { ComponentProps, useMemo } from "react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import {
  ListGesecResourceRequest,
  useBusinessCentralGesecResourceListQuery,
} from "../api/gesec-resource-api";
import { resourceDisplayname } from "./business-central-gesec-resource-name";

export function BusinessCentralGesecResourceSelect({
  bcCompanyId,
  value,
  ...props
}: { bcCompanyId: string } & ComponentProps<typeof SelectPrimitive.Root>) {
  const items = useBusinessCentralGesecResourceSelectItems(
    { companyId: bcCompanyId },
    value,
  );

  return (
    <Select value={value} {...props}>
      <SelectTrigger className="w-full">
        <SelectValue placeholder="Ressource auswählen" />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {items.map((item) => (
            <SelectItem value={item.value} key={item.value}>
              {item.label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

/**
 * useBusinessCentralResourceSelectItems returns a list of
 * resources transformed to be used in <Select> components.
 *
 * Passing the value parameter allows keeping a resource number
 * that is not part of the current Business Central resource list
 * selectable, so an already persisted value stays visible in select.
 */
export function useBusinessCentralGesecResourceSelectItems(
  request: ListGesecResourceRequest,
  value?: string,
): { value: string; label: string }[] {
  const { data } = useBusinessCentralGesecResourceListQuery(request, {
    skip: !request.companyId,
  });

  const items = useMemo(
    () =>
      (data?.value ?? []).map((resource) => ({
        value: resource.no,
        label: resourceDisplayname(resource),
      })),
    [data?.value],
  );

  if (value) {
    if (value === "" || items.some((item) => item.value === value)) {
      return items;
    }
    return [{ value, label: value }, ...items];
  }

  return items;
}

// selectItemsWithValue keeps a resource number that is not part of the
// current Business Central resource list selectable, so an already
// persisted value stays visible in the select.
export function selectItemsWithValue(
  items: { value: string; label: string }[],
  value: string,
) {
  if (value === "" || items.some((item) => item.value === value)) {
    return items;
  }
  return [{ value, label: value }, ...items];
}
